/**
 * Keep profile formatting shared across the collaboration page, connection profiles, and onboarding
 * previews so the same stored document renders consistently.
 */
import type { ProfileEntry, ProfileLine } from "~vue/components/ProfileTopicSection.vue";
import { i18n } from "~vue/i18n";
import { DEFAULT_ICON, SUBTOPIC_ICONS } from "~vue/utils/collabIcons";

function splitContentLines(content: string): ProfileLine[] {
    return content
        .split(/\r?\n/)
        .map((line) => {
            const trimmed = line.trim();
            const isBullet = /^[-•]\s*/.test(trimmed);
            return { text: isBullet ? trimmed.replace(/^[-•]\s*/, "") : trimmed, isBullet };
        })
        .filter((line) => Boolean(line.text));
}

// Subtopic titles and placeholders are seeded in English on the backend (accounts migration 0142), and
// the title is the only identifier the API exposes. Unknown titles fall back to the stored text.
const t = i18n.global.t;
const SUBTOPIC_COPY: Record<string, () => { title: string; placeholder: string }> = {
    "Communication Style": () => ({
        title: t("profile.collaboration.subtopics.communicationStyle.title"),
        placeholder: t("profile.collaboration.subtopics.communicationStyle.placeholder"),
    }),
    "Decision-Making Style": () => ({
        title: t("profile.collaboration.subtopics.decisionMaking.title"),
        placeholder: t("profile.collaboration.subtopics.decisionMaking.placeholder"),
    }),
    "Information Processing": () => ({
        title: t("profile.collaboration.subtopics.informationProcessing.title"),
        placeholder: t("profile.collaboration.subtopics.informationProcessing.placeholder"),
    }),
    "Work Structure": () => ({
        title: t("profile.collaboration.subtopics.workStructure.title"),
        placeholder: t("profile.collaboration.subtopics.workStructure.placeholder"),
    }),
    "Feedback & Collaboration": () => ({
        title: t("profile.collaboration.subtopics.feedbackCollaboration.title"),
        placeholder: t("profile.collaboration.subtopics.feedbackCollaboration.placeholder"),
    }),
    "Motivators & Stressors": () => ({
        title: t("profile.collaboration.subtopics.motivatorsStressors.title"),
        placeholder: t("profile.collaboration.subtopics.motivatorsStressors.placeholder"),
    }),
    "What goals really matter to me right now": () => ({
        title: t("profile.collaboration.subtopics.goals.title"),
        placeholder: t("profile.collaboration.subtopics.goals.placeholder"),
    }),
    "When I've seen teams at their best, what was happening": () => ({
        title: t("profile.collaboration.subtopics.teamsAtTheirBest.title"),
        placeholder: t("profile.collaboration.subtopics.teamsAtTheirBest.placeholder"),
    }),
    "One thing that erodes trust with me quickly": () => ({
        title: t("profile.collaboration.subtopics.trustErosion.title"),
        placeholder: t("profile.collaboration.subtopics.trustErosion.placeholder"),
    }),
    "What impresses me in a direct report": () => ({
        title: t("profile.collaboration.subtopics.directReport.title"),
        placeholder: t("profile.collaboration.subtopics.directReport.placeholder"),
    }),
    "How and when I want to learn about issues": () => ({
        title: t("profile.collaboration.subtopics.learningAboutIssues.title"),
        placeholder: t("profile.collaboration.subtopics.learningAboutIssues.placeholder"),
    }),
    "Working agreements I try to hold us to": () => ({
        title: t("profile.collaboration.subtopics.workingAgreements.title"),
        placeholder: t("profile.collaboration.subtopics.workingAgreements.placeholder"),
    }),
};

export interface ProfileSubtopicSource {
    id: string;
    title: string;
    content: string;
    placeholder?: string;
}

export function toProfileEntry({ id, title, content, placeholder = "" }: ProfileSubtopicSource): ProfileEntry {
    const trimmed = content.trim();
    const lines = trimmed ? splitContentLines(trimmed) : [];
    const copy = SUBTOPIC_COPY[title]?.();
    return {
        id,
        title: copy?.title ?? title,
        content: trimmed,
        // Single-line content previously rendered an unwanted bullet marker where the text was intended as
        // a sentence.
        lines: lines.length > 1 ? lines : trimmed ? [{ text: trimmed, isBullet: false }] : [],
        icon: SUBTOPIC_ICONS[title] ?? DEFAULT_ICON,
        placeholder: copy?.placeholder ?? placeholder,
    };
}
